import React from 'react';
import { useQuery } from 'react-query';
import {
  createBrowserRouter,
  createRoutesFromElements,
  Navigate,
  Outlet,
  Route,
  RouterProvider,
} from 'react-router-dom';

import * as Sentry from '@sentry/react';

import { getUniques } from 'apis/CommonAPI';
import ActionCenterPage from 'components/ActionCenter/ActionCenterPage';
import ActionPlans from 'components/ActionPlans';
import AiCompetitiveIntelligence from 'components/AiCompetitiveIntelligence';
import Alerts from 'components/Alerts';
import AmenitiesFetchForm from 'components/AmenitiesFetchForm/AmenitiesFetchForm';
import CmiDigestThankYou from 'components/CmiDigestThankYou/CmiDigestThankYou';
import ComingSoonFeaturePage from 'components/ComingSoonFeatures/ComingSoonFeaturePage';
import CommunitiesToWatch from 'components/CommunitiesToWatch';
import CommunityAIOverview from 'components/CommunityAIOverview/CommunityAIOverview';
import Companies from 'components/Companies/Companies';
import CompanyAIOverview from 'components/CompanyAIOverview/CompanyAIOverview';
import EmailMatching from 'components/EmailMatching';
import GoogleMatrixReviews from 'components/GoogleMetricsReviews';
import Groups from 'components/Groups';
import IncentiveScrappingRoot from 'components/IncentiveScrapping/IncentiveScrappingRoot';
import PrivateRoot from 'components/layout/PrivateRoot';
import RingCentralRecords from 'components/MysteryShopping/RingCentralRecords';
import ShopperEmails from 'components/MysteryShopping/ShopperEmails';
import ShopRequests from 'components/MysteryShopping/ShopRequests';
import ShopRequestsOverview from 'components/MysteryShopping/ShopRequestsOverview';
import Overview from 'components/Overview/Overview';
import Recommendations from 'components/Recommendations';
import SalesTalkTracks from 'components/SalesTalkTracks';
import CmiDigest from 'components/Settings/EmailNotifications/CmiDigest/CmiDigest';
import CommunityReviewAlerts from 'components/Settings/EmailNotifications/CommunityReviewAlerts/CommunityReviewAlerts';
import CompetitorIncentive from 'components/Settings/EmailNotifications/CompetitorIncentives/CompetitorIncentive';
import MarketIntelligenceDigest from 'components/Settings/EmailNotifications/MarketIntelligenceDigest/MarketIntelligenceDigest';
import MarketRateComparisonUpdates from 'components/Settings/EmailNotifications/MarketRateComparisonUpdates/MarketRateComparisonUpdates';
import OnlineReviewsDigest from 'components/Settings/EmailNotifications/OnlineReviewsDigest/OnlineReviewsDigest';
import SalesBattlecardDigest from 'components/Settings/EmailNotifications/SalesBattlecardDigest/SalesBattlecard';
import SpreadsheetExport from 'components/Settings/GeneralSettings/SpreadsheetExport/SpreadsheetExport';
import ReminderToUpdatePricing from 'components/Settings/ReminderToUpdatePricing';
import Settings from 'components/Settings/Settings';
import SettingsOverview from 'components/Settings/SettingsOverview';
import SpeedToLead from 'components/SpeedToLead';
import TaskQueue from 'components/TaskQueue/TaskQueue';
import UserManagementRoot from 'components/UserManagement/UserManagementRoot';
import { ACTION_CENTER_ROUTE } from 'constants/actionCenter';
import SwotSurvey from 'pages/SwotSurvey/Swot';
import './assets/styles/main.css';
import SwotList from 'pages/SwotSurvey/SwotList';
import { useAppDispatch, useAppSelector } from 'store/hook';
import { handleSetOptionTypes } from 'store/slices/globalSlice';

import Auth0Callback from './auth/Auth0Callback';
import ProtectedRoute from './common/Route/ProtectedRoute';
import Communities from './components/Communities/Communities';
import Community from './components/Community';
import Competitor from './components/Community/Competitor';
import CommunityInfoRoot from './components/CommunityInfo';
import Incentives from './components/Incentives/Incentives';
import IncentivesOverview from './components/Incentives/IncentivesOverview';
import DatadogRumBoundary from './components/layout/DatadogRumBoundary';
import OverviewContent from './components/Overview/OverviewContent';
import Publication from './components/Publication';
import PublicationView from './components/Publication/PublicationView';
import CommunityViewRoot from './components/View/CommunityViewRoot';
import CommunityViewWrapper from './components/View/CommunityViewWrapper';
import CompetitorViewWrapper from './components/View/CompetitorViewWrapper';
import {
  canAccessActionPlansCompanyView,
  canAccessAICompetitiveIntelligence,
  canAccessAISpeedToLead,
  canAccessComingSoonFeatures,
  canAccessCompanyAIOverview,
  canAccessSalesBattlecardContent,
  canManageShopperEmails,
  canViewActionCenter,
  canViewPricingData,
  canViewRingCentralRecords,
} from './helpers/permissions';
import ForgotPassword from './pages/Authentication/ForgotPassword';
import SignIn from './pages/Authentication/SignIn';

const sentryCreateBrowserRouter = Sentry.wrapCreateBrowserRouter(createBrowserRouter);

function App() {
  const dispatch = useAppDispatch();
  const { currentUser } = useAppSelector((state) => state.auth);
  const { communities } = useAppSelector((state) => state.global);
  const canViewSalesBattlecardContent = canAccessSalesBattlecardContent(currentUser);
  const canViewShopperEmails = canManageShopperEmails(currentUser);
  const showPricingData = canViewPricingData(currentUser);
  const showAICompetitiveIntelligence = canAccessAICompetitiveIntelligence(currentUser);
  const showSpeedToLead = canAccessAISpeedToLead(currentUser);
  const showComingSoonFeatures = canAccessComingSoonFeatures(currentUser);
  const showCompanyAIOverview = canAccessCompanyAIOverview(currentUser, communities.length);
  const hasPricingDataAccess = Boolean(currentUser?.isSuperuser || currentUser?.feature_flags?.view_pricing_data);
  const hasAICompetitiveIntelligence = Boolean(currentUser?.feature_flags?.show_ai_competitive_intelligence);
  const canViewAllSettings = Boolean(currentUser?.isSuperuser || currentUser?.feature_flags?.view_pricing_data);
  const canViewRingCentralRecordsPage = canViewRingCentralRecords(currentUser);
  const canViewActionCenterPage = canViewActionCenter(currentUser);
  const showActionPlansCompanyView = canAccessActionPlansCompanyView(currentUser) && communities.length > 1;
  const hasCmiDigestEnabled = Boolean(currentUser?.cmi_digest_preference?.enabled);
  const isFurtherAdmin = Boolean(currentUser?.isSuperuser || currentUser?.isStaff);
  const showCmiDigestSettings = Boolean(currentUser?.feature_flags?.show_cmi_digest) || isFurtherAdmin;

  const router = sentryCreateBrowserRouter(
    createRoutesFromElements(
      <>
        <Route element={<DatadogRumBoundary />}>
          <Route path="sign-in" element={<SignIn />} />
          <Route path="auth/callback" element={<Auth0Callback />} />
          <Route path="forgot-password" element={<ForgotPassword />} />
          <Route path="public/:hash" element={<Publication />}>
            <Route path="view/:viewId" element={<PublicationView />} />
          </Route>
          <Route path="cmi-digest/thank-you" element={<CmiDigestThankYou />} />
          <Route path="/" element={<PrivateRoot />}>
            {currentUser && !currentUser.isSuperuser && (
              <>
                <Route path="overview" element={<Overview />}>
                  <Route path="" element={<OverviewContent />} />
                </Route>
                <Route path="community/:communityId" element={<Community />}>
                  {hasAICompetitiveIntelligence && <Route path="ai-overview" element={<CommunityAIOverview />} />}
                  <Route path="info" element={<CommunityInfoRoot />} />
                  <Route path="alerts" element={<Alerts.List />} />
                  <Route path="alerts/:alertId" element={<Alerts.Details />} />
                  <Route path="action-plans" element={<ActionPlans.List />} />
                  <Route path="action-plans/:actionPlanId" element={<ActionPlans.Details />} />
                  <Route path="view" element={<CommunityViewRoot />}>
                    <Route path=":viewId" element={<CommunityViewWrapper />} />
                  </Route>
                  <Route path="competitor" element={<Competitor />}>
                    <Route path=":competitorId/info" element={<CommunityInfoRoot />} />
                  </Route>
                </Route>
                <Route path="competitor/:competitorId" element={<Community />}>
                  <Route path="info" element={<CommunityInfoRoot />} />
                  <Route path="view" element={<CommunityViewRoot />}>
                    <Route path=":viewId" element={<CompetitorViewWrapper />} />
                  </Route>
                </Route>
                {showPricingData && (
                  <Route path="recommendations" element={<Recommendations.Root />}>
                    <Route path="" element={<Recommendations.List />} />
                    <Route path=":recommendationId" element={<Recommendations.Details />} />
                  </Route>
                )}
                {showCompanyAIOverview && (
                  <Route path="ai-overview" element={<AiCompetitiveIntelligence.Root />}>
                    <Route index element={<CompanyAIOverview />} />
                  </Route>
                )}
                {(showAICompetitiveIntelligence || showSpeedToLead || showComingSoonFeatures) && (
                  <Route path="ai-competitive-intelligence" element={<AiCompetitiveIntelligence.Root />}>
                    <Route index element={<AiCompetitiveIntelligence.IndexRedirect />} />
                    <Route element={<AiCompetitiveIntelligence.SwitcherLayout />}>
                      {showAICompetitiveIntelligence && (
                        <Route path="overall-market-position" element={<AiCompetitiveIntelligence.List />} />
                      )}
                      {showSpeedToLead && <Route path="speed-to-lead" element={<SpeedToLead.Route />} />}
                      {showComingSoonFeatures && (
                        <Route path="coming-soon/:feature" element={<ComingSoonFeaturePage />} />
                      )}
                    </Route>
                    {showAICompetitiveIntelligence && (
                      <Route path=":recommendationId" element={<AiCompetitiveIntelligence.Details />} />
                    )}
                    {showSpeedToLead && (
                      <Route path="speed-to-lead/:recommendationId" element={<SpeedToLead.Details />} />
                    )}
                  </Route>
                )}
                <Route
                  path="communities-to-watch"
                  element={<ProtectedRoute condition={hasCmiDigestEnabled} component={<CommunitiesToWatch.Root />} />}
                />
                <Route
                  path="communities-to-watch/:runId/communities/:runCommunityId"
                  element={
                    <ProtectedRoute condition={hasCmiDigestEnabled} component={<CommunitiesToWatch.Details />} />
                  }
                />
                <Route path="sales-talk-tracks" element={<SalesTalkTracks />} />
                {showActionPlansCompanyView && (
                  <Route path="action-plans-company" element={<ActionPlans.CompanyView />} />
                )}
              </>
            )}
            <Route path="companies" element={<Companies />} />
            <Route path="communities" element={<Communities />} />
            <Route path="communities/:communityId/info" element={<CommunityInfoRoot />} />
            <Route path="groups" element={<Groups />} />
            <Route path="users" element={<UserManagementRoot />} />
            <Route
              path="email-matching"
              element={<ProtectedRoute condition={Boolean(currentUser?.isSuperuser)} component={<EmailMatching />} />}
            />
            <Route
              path="incentive-scrapping"
              element={
                <ProtectedRoute condition={Boolean(currentUser?.isSuperuser)} component={<IncentiveScrappingRoot />} />
              }
            />
            <Route
              path="task-queue"
              element={<ProtectedRoute condition={Boolean(currentUser?.isSuperuser)} component={<TaskQueue />} />}
            />
            <Route
              path={ACTION_CENTER_ROUTE.SEGMENT}
              element={<ProtectedRoute condition={canViewActionCenterPage} component={<ActionCenterPage />} />}
            />
            {hasPricingDataAccess && (
              <Route path="proposals" element={<Incentives />}>
                <Route path="" element={<IncentivesOverview />} />
              </Route>
            )}
            <Route path="preferences" element={<Settings />}>
              <Route path="" element={<SettingsOverview />} />
              <Route
                path="cmi-digest"
                element={<ProtectedRoute condition={showCmiDigestSettings} component={<CmiDigest />} />}
              />
              <Route path="online-reviews-digest" element={<OnlineReviewsDigest />} />
              <Route path="community-review-alerts" element={<CommunityReviewAlerts />} />
              {canViewAllSettings && (
                <>
                  <Route path="market-intelligence-digest" element={<MarketIntelligenceDigest />} />
                  <Route
                    path="sales-battlecard-digest"
                    element={
                      <ProtectedRoute condition={canViewSalesBattlecardContent} component={<SalesBattlecardDigest />} />
                    }
                  />
                  <Route path="market-rate-comparison-updates" element={<MarketRateComparisonUpdates />} />
                  <Route path="competitor-incentive-updates" element={<CompetitorIncentive />} />
                  <Route path="reminder-to-update-pricing" element={<ReminderToUpdatePricing />} />
                  <Route path="spreadsheet-export" element={<SpreadsheetExport />} />
                </>
              )}
            </Route>
            <Route path="mystery-shopping" element={<ShopRequests />}>
              <Route path="" element={<ShopRequestsOverview />} />
              <Route
                path="shopper-emails"
                element={<ProtectedRoute condition={canViewShopperEmails} component={<ShopperEmails />} />}
              />
              <Route
                path="ringcentral-records"
                element={
                  <ProtectedRoute condition={canViewRingCentralRecordsPage} component={<RingCentralRecords />} />
                }
              />
            </Route>
            <Route path="swot" element={<Outlet />}>
              <Route path="swot-survey" element={<SwotSurvey />} />
              <Route path="swot-survey-list" element={<SwotList />} />
            </Route>
            <Route path="google-metrics-reviews/*" element={<GoogleMatrixReviews />} />
            <Route path="amenities" element={<AmenitiesFetchForm />} />
          </Route>
          <Route path="*" element={<Navigate to="/" replace />} />
        </Route>
      </>
    )
  );

  // get apartment types
  useQuery({
    queryKey: 'uniques',
    queryFn: () => getUniques(),
    onSuccess: (data) => {
      const formatted_types = {
        feePricingTypes: data?.FeePricingTypes,
        apartmentTypes: data?.ApartmentTypes,
        apartmentTypeGroups: data?.ApartmentTypeGroups?.map((item: any) => ({
          id: item.id,
          name: item.name,
          apartmentTypes: item.apartment_types,
        })),
        livingTypes: data?.LivingTypes,
        contracts: data?.Contracts,
        reviewCategories: data?.reviewCategories,
        positions: data?.Positions,
        loading: false,
      };
      dispatch(handleSetOptionTypes(formatted_types));
    },
    refetchOnMount: false,
    refetchOnWindowFocus: false,
  });

  return <RouterProvider router={router} />;
}

export default App;
